import React, { Component } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { apiPath, imageURL } from '../../utils/api'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons'
import './PhotoVerificationModal.scss'

class PhotoVerificationModal extends Component {
  constructor(props) {
    super(props)
    this.state = {
      guests: [],
      reservationInfo: null,
      selectedGuestIndex: 0,
      loading: true,
      completing: false,
    }
    this.handleVerifyGuest = this.handleVerifyGuest.bind(this)
    this.handleComplete = this.handleComplete.bind(this)
  }

  componentDidMount() {
    this.fetchGuests()
  }

  fetchGuests() {
    const { call } = this.props
    const reservationId = call && call.reservation && call.reservation.id
    if (!reservationId) {
      this.setState({ loading: false })
      return
    }
    axios.get(apiPath(`reservations/${reservationId}/guests`))
      .then((res) => {
        this.setState({
          guests: res.data.guests || [],
          reservationInfo: res.data.reservation_info || null,
          loading: false,
        })
      })
      .catch(() => {
        this.setState({ loading: false })
      })
  }

  formatDate(dateStr) {
    if (!dateStr) return '-'
    const d = new Date(dateStr)
    if (isNaN(d)) return dateStr
    return d.toLocaleDateString('ja-JP')
  }

  handleVerifyGuest(guestId) {
    // mark verified immediately so the UI feels instant.
    this.setState((prevState) => ({
      guests: prevState.guests.map((guest) =>
        guest.id === guestId ? { ...guest, status: 'verified' } : guest
      ),
    }))

    axios.put(apiPath(`guests/${guestId}/verify`), { status: 'verified' })
      .catch(() => {
        this.setState((prevState) => ({
          guests: prevState.guests.map((guest) =>
            guest.id === guestId ? { ...guest, status: 'pending' } : guest
          ),
        }))
      })
  }

  handleComplete() {
    const { call, onClose, onComplete } = this.props
    this.setState({ completing: true })
    axios.put(apiPath(`calls/${call.id}`), { call: { status: 'completed' } })
      .then(() => {
        this.setState({ completing: false })
        if (onComplete) onComplete(call)
        else if (onClose) onClose()
      })
      .catch(() => {
        this.setState({ completing: false })
      })
  }

  renderStatusIcon(status) {
    switch (status) {
      case 'verified':
        return <span className="pvm-status-icon pvm-status-verified"><FontAwesomeIcon icon={faCheck} /></span>
      case 'failed':
        return <span className="pvm-status-icon pvm-status-failed">!</span>
      default:
        return <span className="pvm-status-icon pvm-status-pending" />
    }
  }

  renderGuestList() {
    const { guests, selectedGuestIndex } = this.state
    if (!guests.length) {
      return <div className="pvm-no-guests">ゲスト情報なし</div>
    }
    return (
      <div className="pvm-guest-list">
        <div className="pvm-guest-list-header">ゲストの本人確認</div>
        <div>
          {guests.map((guest, index) => (
            <div
              key={guest.id}
              className={`pvm-guest-row ${index === selectedGuestIndex ? 'active' : ''} ${guest.status === 'verified' ? 'verified' : ''}`}
              onClick={() => this.setState({ selectedGuestIndex: index })}
            >
              <div className="pvm-guest-name">
                {this.renderStatusIcon(guest.status)}
                <span>{guest.full_name}</span>
              </div>
              {guest.status !== 'verified' && (
                <div className="pvm-guest-actions">
                  <button
                    className="pvm-btn-ignore"
                    title="無視する"
                    onClick={(e) => { e.stopPropagation() }}
                  >
                    !
                  </button>
                  <button
                    className="pvm-btn-verify"
                    title="確認する"
                    onClick={(e) => { e.stopPropagation(); this.handleVerifyGuest(guest.id) }}
                  >
                    <FontAwesomeIcon icon={faCheck} />
                  </button>
                </div>
              )}
              {guest.status === 'verified' && (
                <div className="pvm-guest-verified-label">
                  ✓ 確認済み
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  renderPhoto() {
    const { call } = this.props
    const photoUrl = call && call.photo_url
    if (!photoUrl) {
      return <div className="pvm-no-photo">写真がありません</div>
    }
    const fullUrl = photoUrl.startsWith('http') ? photoUrl : imageURL(photoUrl)
    return <img src={fullUrl} alt="Verification" className="pvm-photo" />
  }

  renderReservationInfo() {
    const { reservationInfo } = this.state
    const { call } = this.props
    if (!reservationInfo) return null

    const reservationCode = call && call.reservation && call.reservation.code
    const listingPhotoUrl = reservationInfo.listing_photo_url
      ? (reservationInfo.listing_photo_url.startsWith('http')
          ? reservationInfo.listing_photo_url
          : imageURL(reservationInfo.listing_photo_url))
      : null

    return (
      <div className="pvm-zens-register">
        <div className="pvm-zens-register-title">Zens Register</div>
        <div className="pvm-zens-register-card">
          <div className="pvm-zr-image">
            {listingPhotoUrl
              ? <img src={listingPhotoUrl} alt="Listing" className="pvm-listing-photo" />
              : <div className="pvm-listing-photo-placeholder" />
            }
          </div>
          <div className="pvm-zr-info">
            <div className="pvm-zr-code">{reservationCode || '-'}</div>
            <div className="pvm-zr-dates">
              <div className="pvm-zr-date-col">
                <strong>チェックイン日</strong>
                <div className="pvm-zr-date-value">{this.formatDate(reservationInfo.checkin_date)}</div>
              </div>
              <div className="pvm-zr-date-col">
                <strong>チェックアウト日</strong>
                <div className="pvm-zr-date-value">{this.formatDate(reservationInfo.checkout_date)}</div>
              </div>
              <div className="pvm-zr-date-col">
                <strong>ゲスト</strong>
                {reservationInfo.number_of_guests
                  ? (reservationInfo.additional_guests > 0
                      ? `${reservationInfo.number_of_guests} (+${reservationInfo.additional_guests})`
                      : reservationInfo.number_of_guests)
                  : '-'}
              </div>
            </div>
            <div className="pvm-zr-listing-name">{reservationInfo.listing_full_name || reservationInfo.listing_short_name || '-'}</div>
          </div>
        </div>
      </div>
    )
  }

  renderGuestDetail() {
    const { guests, selectedGuestIndex } = this.state
    const guest = guests[selectedGuestIndex]
    if (!guest) return <div className="pvm-guest-detail-empty">ゲストを選択してください</div>

    // Address: street lines only (city shown separately)
    const addressParts = [guest.street_1, guest.street_2].filter(Boolean)
    const city = guest.city || ''

    // Passport / identification images — guest_images is an array from ZA API: [{ id, url, face_id }]
    const guestImages = Array.isArray(guest.guest_images)
      ? guest.guest_images
          .map((img) => {
            const raw = img && (img.url || img.image_url)
            if (!raw) return null
            if (typeof raw === 'string' && (raw.startsWith('http') || raw.startsWith('data:'))) return raw
            return imageURL(raw)
          })
          .filter(Boolean)
      : []

    return (
      <div className="pvm-guest-detail">
        <div className="pvm-detail-section">
          <div className="pvm-detail-row">
            <span className="pvm-detail-label">居住国</span>
            <span className="pvm-detail-value">{guest.country || '-'}</span>
          </div>
          {guest.nationality && (
            <div className="pvm-detail-row">
              <span className="pvm-detail-label">国籍</span>
              <div>
                <span className="pvm-detail-value">{guest.nationality}</span>
                {guestImages.length > 0 && (
                  <div className="pvm-nationality-photos">
                    {guestImages.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                        <img src={url} alt={`passport-${i + 1}`} className="pvm-identification-img" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="pvm-detail-row">
            <span className="pvm-detail-label">宿泊者名</span>
            <span className="pvm-detail-value">{guest.full_name || guest.zens_guest_name || '-'}</span>
          </div>
          <div className="pvm-detail-row">
            <span className="pvm-detail-label">性別</span>
            <span className="pvm-detail-value">{guest.gender || '-'}</span>
          </div>
          <div className="pvm-detail-row">
            <span className="pvm-detail-label">生年月日</span>
            <span className="pvm-detail-value">{this.formatDate(guest.date_of_birth)}</span>
          </div>
          <div className="pvm-detail-row">
            <span className="pvm-detail-label">旅券番号</span>
            <span className="pvm-detail-value">{guest.passport_number || '-'}</span>
          </div>
          <div className="pvm-detail-row">
            <span className="pvm-detail-label">メール</span>
            <span className="pvm-detail-value">{guest.email || '-'}</span>
          </div>
          {addressParts.length > 0 && (
            <div className="pvm-detail-row">
              <span className="pvm-detail-label">住所</span>
              <span className="pvm-detail-value">{addressParts.join(', ')}</span>
            </div>
          )}
          {(city || guest.state) && (
            <div className="pvm-detail-row pvm-two-col-row">
              {city && (
                <div className="pvm-col">
                  <span className="pvm-detail-label">市</span>
                  <span className="pvm-detail-value">{city}</span>
                </div>
              )}
              {guest.state && (
                <div className="pvm-col">
                  <span className="pvm-detail-label">都道府県</span>
                  <span className="pvm-detail-value">{guest.state}</span>
                </div>
              )}
            </div>
          )}
          {guest.postal_code && (
            <div className="pvm-detail-row">
              <span className="pvm-detail-label">郵便番号</span>
              <span className="pvm-detail-value">{guest.postal_code}</span>
            </div>
          )}
          <div className="pvm-detail-row">
            <span className="pvm-detail-label">職業</span>
            <span className="pvm-detail-value">{guest.occupation || '-'}</span>
          </div>
          <div className="pvm-detail-row">
            <span className="pvm-detail-label">前泊地</span>
            <span className="pvm-detail-value">{guest.previous_accommodation_location || '-'}</span>
          </div>
          <div className="pvm-detail-row">
            <span className="pvm-detail-label">行先地</span>
            <span className="pvm-detail-value">{guest.next_destination || '-'}</span>
          </div>
        </div>
      </div>
    )
  }

  render() {
    const { onClose } = this.props
    const { loading, completing, guests, selectedGuestIndex } = this.state
    const selectedGuest = guests[selectedGuestIndex]

    return (
      <div className="pvm-overlay" onClick={onClose}>
        <div className="pvm-modal" onClick={(e) => e.stopPropagation()}>
          <div className="pvm-header">
            <span></span>
            <button className="pvm-close-btn" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {loading ? (
            <div className="pvm-loading">Loading...</div>
          ) : (
            <div className="pvm-body">
              <div className="pvm-left-panel">
                {this.renderPhoto()}
                {this.renderGuestList()}
              </div>
              <div className="pvm-right-panel">
                {this.renderReservationInfo()}
                {selectedGuest && (
                  <div className="pvm-guest-name-divider">{selectedGuest.full_name || selectedGuest.zens_guest_name}</div>
                )}
                {this.renderGuestDetail()}
              </div>
            </div>
          )}

          <div className="pvm-footer">
            <button className="pvm-btn-save" onClick={onClose}>SAVE</button>
            <button
              className="pvm-btn-complete"
              onClick={this.handleComplete}
              disabled={completing}
            >
              {completing ? 'PROCESSING...' : 'COMPLETE'}
            </button>
          </div>
        </div>
      </div>
    )
  }
}

PhotoVerificationModal.contextTypes = {
  t: PropTypes.func,
}

export default PhotoVerificationModal
